import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import { EditionButtons } from '../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import { LabelComponent } from '../../../../../../shared/components/form/label/label.component';
import { ErrorService } from '../../../../../../shared/services/error.service';
import { CombosService } from '../../../../../../shared/services/combos.service';
import { FormStatusService } from '../../../../../../shared/services/form-status.service';
import { GenericCombo } from '../../../../../../shared/services/interfaces/combos.interface';
import { SnackbarService } from '@shared/services/snackbar.service';
import { ContactoAdministrativoPayload } from '../contactos-administrativos.interface';
import { ContactosAdministrativosService } from '../contactos-administrativos.service';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { SwitchComponent } from '@shared/components/form/switch/switch.component';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { Modal } from '@shared/components/common/modal/modal';
// Badge quedó sin uso al reemplazar el chip del establecimiento por un select (issue #26).
// import { Badge } from '../../../../../../shared/components/ui/badge/badge';

@Component({
  selector: 'app-detalle-contacto-administrativo',
  imports: [HasPermissionDirective, LabelComponent, ReactiveFormsModule, EditionButtons, SelectFieldComponent, InputFieldComponent, SwitchComponent, AppFormComponent, Modal],
  templateUrl: './detalle-contacto-administrativo.html',
})
export class DetalleContactoAdministrativo implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);
  editForm!: FormGroup;
  loading = false;
  // Se abre directo en edición: Cancelar cierra
  editable = !this.permissionAuth.canEdit('clientes');
  updating = false;
  tiposContactosAdministrativos: GenericCombo[] = [];
  // Issue #26 (2026-09-15): el establecimiento del contacto dejó de ser un dato fijo.
  // Es lo que distingue un contacto administrativo del cliente (FK vacía) de uno de
  // establecimiento (FK cargada), así que cambiarlo mueve el contacto de una lista a
  // la otra. El combo se pide recién con el cli_cliente_id que trae el contacto.
  establecimientos: GenericCombo[] = [];

  get tiposContactosAdministrativosOptions(): SelectOption[] { return this.tiposContactosAdministrativos.map(x => ({ value: x.id, label: x.nombre })); }
  get establecimientosOptions(): SelectOption[] { return this.establecimientos.map(x => ({ value: x.id, label: x.nombre })); }

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private contactosAdministrativosService = inject(ContactosAdministrativosService);
  private dialogRef = inject(MatDialogRef<DetalleContactoAdministrativo>);
  private idContacto = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.getCombos();
    this.makeForm();
    this.getContacto();
  }

  private makeForm(): void {
    this.editForm = this.fB.group({
      cli_cliente_id: [{ value: '', disabled: true }, Validators.required],
      // Vacío = contacto administrativo del cliente; con valor = contacto de ese establecimiento
      cli_establecimiento_id: [{ value: '', disabled: this.editable }],
      param_establecimiento_tipo_contacto_id: [
        { value: '', disabled: this.editable },
        Validators.required,
      ],
      nombre: [{ value: '', disabled: this.editable }, Validators.required],
      apellido: [{ value: '', disabled: this.editable }, Validators.required],
      celular: [{ value: '', disabled: this.editable }, Validators.required],
      email: [{ value: '', disabled: this.editable }, Validators.email],
      activo: [{ value: false, disabled: this.editable }],
    });
  }

  private getCombos(): void {
    this.combosService
      .getEstablecimientosTiposContactosCombo()
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.tiposContactosAdministrativos = response.data;
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      });
  }

  private getContacto(): void {
    this.loading = true;
    this.contactosAdministrativosService
      .show(this.idContacto)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.editForm.patchValue({
              ...response.data,
              cli_establecimiento_id: response.data.cli_establecimiento_id ?? '',
              activo: !!response.data.activo
            });
            this.getEstablecimientos(response.data.cli_cliente_id);
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      })
      .add(() => {
        this.loading = false;
      });
  }

  /**
   * Los establecimientos del cliente dueño del contacto. Se pide después del show
   * porque el diálogo solo recibe el id del contacto, no el del cliente.
   */
  private getEstablecimientos(cliClienteId: number): void {
    this.combosService
      .getEstablecimientosCombo(cliClienteId)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.establecimientos = response.data;
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      });
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  update(): void {
    if (!this.permissionAuth.canEdit('clientes')) return;
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.updating = true;
    const formValue = this.editForm.getRawValue();
    const payload: ContactoAdministrativoPayload = {
      ...formValue,
      // app-select emite string: sin el Number() el backend recibiría "3" y el '' de
      // "ninguno" viajaría como string vacío en vez de null.
      cli_establecimiento_id: formValue.cli_establecimiento_id ? Number(formValue.cli_establecimiento_id) : null,
      email: formValue.email || null,
      activo: !!formValue.activo
    };

    this.contactosAdministrativosService
      .update(this.idContacto, payload)
      .subscribe({
        next: (response) => {
          if (response.status) {
            this.snackbarService.showSnackBar(
              'Contacto actualizado correctamente'
            );
            this.dialogRef.close(true);
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      })
      .add(() => {
        this.updating = false;
      });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
